<script>
    import { slide } from 'svelte/transition';
    import { createEventDispatcher } from 'svelte';
    
    export let errors = [];
    
    const dispatch = createEventDispatcher();
    
    // Debug logging
    $: console.log('[ClaimValidationErrors] Errors prop updated:', errors);
    
    // Group errors by type for better display
    $: blockingErrors = errors.filter(e => e.blocking);
    $: warningErrors = errors.filter(e => !e.blocking);
    
    function dismissError(index) {
        errors = errors.filter((_, i) => i !== index);
        dispatch('dismiss', { index });
    }
</script>

{#if errors.length > 0}
    <div class="validation-errors-container" transition:slide={{ duration: 200 }}>
        {#if blockingErrors.length > 0}
            <div class="error-group blocking-errors">
                <div class="error-group-header">
                    <span class="error-title">Blocking Errors - Must Fix</span>
                </div>
                {#each blockingErrors as error, index}
                    <div class="validation-error error-blocking" transition:slide={{ duration: 150 }}>
                        <span class="error-message">{error.message}</span>
                        <button 
                            class="error-dismiss"
                            on:click={() => dismissError(errors.indexOf(error))}
                            aria-label="Dismiss error"
                        >
                            ×
                        </button>
                    </div>
                {/each}
            </div>
        {/if}
        
        {#if warningErrors.length > 0}
            <div class="error-group warning-errors">
                <div class="error-group-header">
                    <span class="error-title">Warnings</span>
                </div>
                {#each warningErrors as error, index}
                    <div class="validation-error error-warning" transition:slide={{ duration: 150 }}>
                        <span class="error-message">{error.message}</span>
                        <button 
                            class="error-dismiss"
                            on:click={() => dismissError(errors.indexOf(error))}
                            aria-label="Dismiss warning"
                        >
                            ×
                        </button>
                    </div>
                {/each}
            </div>
        {/if}
    </div>
{/if}

<style>
.validation-errors-container {
    margin: var(--spacing-3) 0;
    overflow: hidden;
    border-radius: var(--border-radius-md);
}

.error-group {
    margin-bottom: var(--spacing-2);
}

.error-group:last-child {
    margin-bottom: 0;
}

.error-group-header {
    display: flex;
    align-items: center;
    padding: var(--spacing-2) var(--spacing-3);
    font-size: var(--font-size-sm);
    font-weight: 600;
    background: var(--color-surface-hover);
    gap: var(--spacing-2);
}

.validation-error {
    position: relative;
    display: flex;
    align-items: center;
    padding: var(--spacing-2) var(--spacing-3);
    font-size: var(--font-size-sm);
    background: var(--color-surface);
    border-left: 3px solid;
    gap: var(--spacing-2);
}

.validation-error:hover {
    background: var(--color-surface-hover);
}

.error-blocking {
    border-left-color: var(--color-validation-error-border, var(--color-error));
    background: var(--color-validation-error-bg, var(--color-error-bg-subtle));
}

.error-blocking:hover {
    background: var(--color-validation-error-bg-hover, var(--color-error-bg-subtle-hover));
}

.error-blocking .error-message {
    color: var(--color-validation-error-text);
}

.error-blocking .error-dismiss {
    color: var(--color-validation-error-text);
}

.error-blocking .error-dismiss:hover {
    color: var(--color-validation-error-text);
}

.error-warning {
    border-left-color: var(--color-validation-warning-border, var(--color-warning));
    background: var(--color-validation-warning-bg, var(--color-warning-bg-subtle));
}

.error-warning:hover {
    background: var(--color-validation-warning-bg-hover, var(--color-warning-bg-subtle-hover));
}

.error-warning .error-message {
    color: var(--color-validation-warning-text);
}

.error-warning .error-dismiss {
    color: var(--color-validation-warning-text);
}

.error-warning .error-dismiss:hover {
    color: var(--color-validation-warning-text);
}

.error-title {
    flex: 1;
    font-weight: 600;
    color: var(--color-text-primary);
}

.error-message {
    flex: 1;
    line-height: 1.4;
    color: var(--color-text-primary);
}

.error-dismiss {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    padding: 0;
    font-size: 20px;
    line-height: 1;
    color: var(--color-text-tertiary);
    background: transparent;
    border: none;
    border-radius: var(--border-radius-sm);
    transition: all 0.2s ease;
    cursor: pointer;
}

.error-dismiss:hover {
    color: var(--color-text-primary);
    background: var(--color-surface-hover);
}

.error-dismiss:focus {
    outline: 2px solid var(--color-primary);
    outline-offset: -2px;
}

/* Mobile responsiveness */
@media (width <= 768px) {
    .validation-error {
        flex-wrap: wrap;
    }
    
    .error-message {
        width: 100%;
        margin-top: var(--spacing-1);
    }
}
</style>